// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles, faLanguage, faGraduationCap, faSpellCheck, faBookOpen, faRotate, faNewspaper, faChartLine, faChevronDown, faUserCircle, faEllipsis, faDumbbell, faGlobe, faUserShield, faKeyboard } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import Logo from "../Logo/Logo";
import AchievementUnlockedNotice from "../Achievements/AchievementUnlockedNotice";
import LevelUpNotice from "../Achievements/LevelUpNotice";
import LevelBadge from "./LevelBadge";
import styles from "./header.module.scss";

const moreLinks = [
    { to: "/grammar", icon: faSpellCheck, label: "Grammar" },
    { to: "/converter", icon: faWandMagicSparkles, label: "Converter" },
    { to: "/romaji-converter", icon: faLanguage, label: "Romaji" },
    { to: "/conjugation", icon: faRotate, label: "Conjugation" },
    { to: "/wanikani", icon: faChartLine, label: "WaniKani" },
    { to: "/blog", icon: faNewspaper, label: "Blog" },
    { to: "/jlpt-test-sites", icon: faGraduationCap, label: "JLPT test sites" },
    { to: "/about", icon: null, label: "About" },
];

// The mobile "Show more" panel folds in Vocabulary too, since it doesn't
// fit as a persistent pill once the nav stops scrolling horizontally.
const mobileOverflowLinks = [{ to: "/vocabulary", icon: faBookOpen, label: "Vocabulary" }, ...moreLinks];

// Same breakpoint as the .nav layout switch in header.module.scss - below
// it, Vocabulary/Grammar/etc. fold into the collapsible "Show more" panel
// instead of rendering inline (desktop) or requiring a floating dropdown.
const MOBILE_NAV_QUERY = "(max-width: 1150px)";

function useCloseOnOutsideEvent(isOpen, onClose, triggerRef, extraRef) {
    const ref = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        function handleOutsideEvent(event) {
            if (event.type === "keydown" && event.key !== "Escape") return;
            if (
                event.type === "mousedown" &&
                (ref.current?.contains(event.target) || extraRef?.current?.contains(event.target))
            ) {
                return;
            }
            onClose();
            // Escape closes the menu out from under a keyboard user's focus -
            // send it back to the trigger button rather than dropping to <body>.
            if (event.type === "keydown") triggerRef.current?.focus();
        }

        document.addEventListener("mousedown", handleOutsideEvent);
        document.addEventListener("keydown", handleOutsideEvent);
        return () => {
            document.removeEventListener("mousedown", handleOutsideEvent);
            document.removeEventListener("keydown", handleOutsideEvent);
        };
    }, [isOpen, onClose, triggerRef, extraRef]);

    return ref;
}

const MoreMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const location = useLocation();
    const isActive = moreLinks.some((link) => location.pathname.startsWith(link.to));
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const menuRef = useCloseOnOutsideEvent(isOpen, () => setIsOpen(false), triggerRef, dropdownRef);

    // On mobile the nav is a horizontally-scrolling strip, so this dropdown
    // is portaled to <body> and positioned in fixed coordinates - anchoring
    // it normally (absolute, relative to the trigger) would put it inside
    // the strip's scroll box, which clips anything below the strip's height.
    function handleToggle() {
        setIsOpen((open) => {
            const next = !open;
            if (next && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                // Anchor from the trigger's right edge rather than its left:
                // "More" is the last nav item, so the trigger sits near the
                // right edge of the screen on mobile and a left-anchored
                // dropdown would run off the viewport.
                const right = Math.max(8, window.innerWidth - rect.right);
                setMenuPosition({ top: rect.bottom + 8, right });
            }
            return next;
        });
    }

    // The trigger's position can change out from under an already-computed
    // fixed position - page scroll, the nav strip's own horizontal scroll,
    // or a viewport resize - so just close rather than track it live.
    useEffect(() => {
        if (!isOpen) return undefined;

        function handleReposition() {
            setIsOpen(false);
        }

        window.addEventListener("scroll", handleReposition, { passive: true, capture: true });
        window.addEventListener("resize", handleReposition);
        return () => {
            window.removeEventListener("scroll", handleReposition, { capture: true });
            window.removeEventListener("resize", handleReposition);
        };
    }, [isOpen]);

    return (
        <div className={styles.moreMenu} ref={menuRef}>
            <button
                type="button"
                ref={triggerRef}
                className={isActive ? `${styles.navLink} ${styles.active}` : styles.navLink}
                onClick={handleToggle}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-current={isActive ? "true" : undefined}
            >
                <FontAwesomeIcon icon={faEllipsis} className={styles.navIcon} />
                More
                <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
            </button>
            {isOpen && menuPosition &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={styles.moreMenuDropdown}
                        style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
                        role="menu"
                    >
                        {moreLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                className={({ isActive: linkActive }) =>
                                    linkActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem
                                }
                                onClick={() => setIsOpen(false)}
                                role="menuitem"
                            >
                                {link.icon && <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />}
                                {link.label}
                            </NavLink>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    );
};

const UserMenu = () => {
    const { user, signOut } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const triggerRef = useRef(null);
    const menuRef = useCloseOnOutsideEvent(isOpen, () => setIsOpen(false), triggerRef);

    async function handleSignOut() {
        setIsOpen(false);
        await signOut();
        navigate("/");
    }

    return (
        <div className={styles.userMenu} ref={menuRef}>
            <button
                type="button"
                ref={triggerRef}
                className={styles.userMenuButton}
                onClick={() => setIsOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <FontAwesomeIcon icon={faUserCircle} className={styles.navIcon} />
                {user?.displayName || user?.email}
                <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
            </button>
            {isOpen && (
                <div className={styles.userMenuDropdown} role="menu">
                    <NavLink to="/profile" className={styles.userMenuItem} onClick={() => setIsOpen(false)} role="menuitem">
                        Profile
                    </NavLink>
                    <button type="button" className={styles.userMenuItem} onClick={handleSignOut} role="menuitem">
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

// "Practice" isn't its own route - it's a section on the homepage. There's
// no scroll-spy, so it's only shown as active when the URL literally points
// at the anchor (matches the precision NavLink would give a real route).
const PracticeNavLink = () => {
    const location = useLocation();
    const isActive = location.pathname === "/" && location.hash === "#practice";

    return (
        <Link
            to="/#practice"
            className={isActive ? `${styles.navLink} ${styles.active}` : styles.navLink}
            aria-current={isActive ? "page" : undefined}
        >
            <FontAwesomeIcon icon={faDumbbell} className={styles.navIcon} />
            Practice
        </Link>
    );
};

const AdminStatsLink = ({ inOverflow = false }) => {
    const { status, user, isSigningOut } = useAuth();
    const { hostname } = window.location;
    // Both the new and old admin hostnames are checked during the domain
    // migration - see aws-config.ts for why the old one is still listed.
    if ((hostname !== "admin.nihongotech.teqseq.com" && hostname !== "admin.kanalearn.teqseq.com") ||
        status !== "authenticated" || !user?.isAdmin || isSigningOut) return null;

    return (
        <NavLink
            to="/stats"
            aria-label="Admin stats"
            title="Signed in as an administrator — view statistics"
            className={({ isActive }) => {
                if (inOverflow) {
                    return isActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem;
                }
                return isActive ? `${styles.navLink} ${styles.active}` : styles.navLink;
            }}
        >
            <FontAwesomeIcon icon={faUserShield} className={styles.navIcon} aria-hidden="true" />
            Admin stats
        </NavLink>
    );
};

const AuthArea = () => {
    const { status, isSigningOut } = useAuth();

    if (status === "loading" || isSigningOut) {
        return (
            <div className={styles.authLoading} role="status">
                <span className={styles.authSpinner} aria-hidden="true" />
                <span className={styles.srOnly}>{isSigningOut ? "Signing out…" : "Loading your account…"}</span>
            </div>
        );
    }

    if (status === "authenticated") {
        return <div className={styles.authLinks}><LevelBadge /><UserMenu /></div>;
    }

    return (
        <div className={styles.authLinks}>
            <NavLink to="/login" className={styles.navLink}>
                Log in
            </NavLink>
            <NavLink to="/register" className={styles.signUpLink}>
                Sign up
            </NavLink>
        </div>
    );
};

// Every page renders exactly one <main>, so the skip link can jump straight
// to it without needing a matching id added to every page component.
function handleSkipToContent(event) {
    event.preventDefault();
    const main = document.querySelector("main");
    if (!main) return;
    if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    main.focus();
}

// Hides the header on downward scroll and brings it back on upward scroll (or
// near the top of the page), so it doesn't permanently occupy a big chunk of
// a small screen. Scrolls under a `revealAt` threshold near the top never
// hide it, and small jitters below `deltaThreshold` are ignored so the header
// doesn't flicker on trackpad/momentum scrolling.
function useHideOnScroll() {
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const revealAt = 96;
        const deltaThreshold = 8;

        function handleScroll() {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const currentY = window.scrollY;
                const delta = currentY - lastY;

                if (currentY <= revealAt) {
                    setHidden(false);
                } else if (Math.abs(delta) > deltaThreshold) {
                    setHidden(delta > 0);
                }

                lastY = currentY;
                ticking = false;
            });
        }

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return [hidden, setHidden];
}

const Header = () => {
    const wrapperRef = useRef(null);
    const location = useLocation();
    const [hidden, setHidden] = useHideOnScroll();
    const isMobileNav = useMediaQuery(MOBILE_NAV_QUERY);
    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const isOverflowActive = mobileOverflowLinks.some((link) => location.pathname.startsWith(link.to));

    useEffect(() => {
        const node = wrapperRef.current;
        if (!node || typeof ResizeObserver === "undefined") return undefined;

        const setHeaderHeightVar = () => {
            document.documentElement.style.setProperty("--header-height", `${node.offsetHeight}px`);
        };

        setHeaderHeightVar();
        const observer = new ResizeObserver(setHeaderHeightVar);
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    // Land on every new page with the header visible rather than stuck hidden
    // from scroll position carried over off the previous page.
    useEffect(() => {
        setHidden(false);
    }, [location.pathname, setHidden]);

    // Following a link inside the panel should close it rather than leaving
    // it expanded over the next page.
    useEffect(() => {
        setIsOverflowOpen(false);
    }, [location.pathname]);

    return (
        <>
            <AchievementUnlockedNotice />
            <LevelUpNotice />
            <a href="#main-content" className={styles.skipLink} onClick={handleSkipToContent}>
                Skip to main content
            </a>
            <header
                className={hidden ? `${styles.wrapper} ${styles.hidden}` : styles.wrapper}
                ref={wrapperRef}
            >
                <div className={styles.header}>
                    <NavLink to="/" className={styles.brandLink} aria-label="NihongoTech by TeqSeq home">
                        <Logo />
                    </NavLink>

                    <nav className={styles.nav} aria-label="Main navigation">
                        <NavLink
                            to="/lessons"
                            className={({ isActive }) =>
                                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                            }
                        >
                            <FontAwesomeIcon icon={faGraduationCap} className={styles.navIcon} />
                            Learn
                        </NavLink>
                        <PracticeNavLink />
                        <NavLink
                            to="/keyboard"
                            className={({ isActive }) =>
                                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                            }
                        >
                            <FontAwesomeIcon icon={faKeyboard} className={styles.navIcon} />
                            Keyboard
                        </NavLink>
                        <NavLink
                            to="/translate"
                            className={({ isActive }) =>
                                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                            }
                        >
                            <FontAwesomeIcon icon={faGlobe} className={styles.navIcon} />
                            Translate
                        </NavLink>
                        {isMobileNav ? (
                            <button
                                type="button"
                                className={
                                    isOverflowActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                                }
                                onClick={() => setIsOverflowOpen((open) => !open)}
                                aria-expanded={isOverflowOpen}
                                aria-controls="mobile-nav-overflow"
                            >
                                {isOverflowOpen ? "Show less" : "Show more"}
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={isOverflowOpen ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
                                />
                            </button>
                        ) : (
                            <>
                                <NavLink
                                    to="/vocabulary"
                                    className={({ isActive }) =>
                                        isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                                    }
                                >
                                    <FontAwesomeIcon icon={faBookOpen} className={styles.navIcon} />
                                    Vocabulary
                                </NavLink>
                                <AdminStatsLink />
                                <MoreMenu />
                            </>
                        )}
                    </nav>

                    <AuthArea />
                </div>
                {isMobileNav && isOverflowOpen && (
                    <div id="mobile-nav-overflow" className={styles.navOverflowPanel}>
                        <AdminStatsLink inOverflow />
                        {mobileOverflowLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                className={({ isActive }) =>
                                    isActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem
                                }
                            >
                                {link.icon && <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />}
                                {link.label}
                            </NavLink>
                        ))}
                    </div>
                )}
            </header>
        </>
    );
};

export default Header;
